import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faMoneyBillWave,
  faFileInvoice,
  faArrowRightArrowLeft,
  faIdCard,
  faGift,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { toPersianDigit } from '@/helpers/utils';
import { useGetWalletBalanceQuery } from '@/app/Wallet/api/walletApi';
import useDomain from '@/hooks/useDomain';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import ChargeWalletModal from './ChargeWalletModal';
import GiftCodeModal from '../../../components/navbar/top/GiftCodeModal';
import LegalModalContainer from '@/app/Purchase/components/LegalModal';

const personalWalletHelp =
  'این کیف پول شخصی شماست و فقط خودتان به موجودی و تراکنش‌های آن دسترسی دارید. برای خریدهای بعدی می‌توانید از این مبلغ استفاده کنید.';
const aiWalletHelp =
  'این اعتبار مشترک دامنه است؛ همه اعضای دارای دسترسی به دامنه می‌توانند آن را ببینند و از آن استفاده کنند.';

type OpenMenu = 'personal' | 'ai' | null;

const personalMenuItems = [
  {
    title: 'پرداخت و افزایش اعتبار',
    action: 'openChargeModal' as const,
    icon: faMoneyBillWave,
  },
  {
    title: 'تراکنش ها',
    to: '/wallet/transactions?wallet_type=1',
    icon: faArrowRightArrowLeft,
  },
  {
    title: 'سفارشات طرح',
    to: '/orders/',
    icon: faFileInvoice,
  },
  {
    title: 'مشخصات مالی',
    action: 'openLegalModal' as const,
    icon: faIdCard,
  },
];

const aiMenuItems = [
  {
    title: 'شارژ اعتبار AI',
    to: '/prorogate-or-upgrade/?wallet=500000',
    icon: faMoneyBillWave,
  },
  {
    title: 'کد هدیه',
    action: 'openGiftCodeModal' as const,
    icon: faGift,
  },
  {
    title: 'تراکنش‌های AI',
    to: '/wallet/transactions?wallet_type=2',
    icon: faArrowRightArrowLeft,
  },
];

const dividerStyle = {
  width: 1,
  backgroundColor: 'var(--falcon-gray-300)',
  opacity: 0.6,
} as const;

function formatBalance(amount: number | string | null | undefined) {
  const withSeparators =
    amount?.toString()?.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') ?? '';
  return toPersianDigit(withSeparators);
}

function BalanceAmount({
  amount,
  loading,
}: {
  amount: number;
  loading?: boolean;
}) {
  if (loading) {
    return <TextLoading width={56} height={14} isWidthPx />;
  }

  return (
    <span className="d-inline-flex align-items-baseline gap-1 fs--2 fw-semibold text-1000 text-nowrap">
      <span dir="ltr">{formatBalance(amount)}</span>
      <span className="fw-medium text-600" style={{ fontSize: '0.65rem' }}>ت</span>
    </span>
  );
}

function WalletDivider() {
  return (
    <span
      aria-hidden
      className="align-self-stretch my-2"
      style={dividerStyle}
    />
  );
}

const WalletsNavGroup = () => {
  const { domain_slug } = useDomain();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showGiftCodeModal, setShowGiftCodeModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);

  const {
    data: walletBalanceResponse,
    isLoading,
    isError,
  } = useGetWalletBalanceQuery(
    { domain_slug: domain_slug ?? '' },
    { skip: !domain_slug }
  );

  const personalBalance =
    !isError && walletBalanceResponse?.personalBalance != null
      ? walletBalanceResponse.personalBalance
      : 0;
  const aiBalance =
    !isError && walletBalanceResponse?.aiBalance != null
      ? walletBalanceResponse.aiBalance
      : 0;
  const balanceLoading = isLoading || isError;

  return (
    <>
      <div className="d-inline-flex align-items-stretch border border-1 bg-white dark__bg-1000 rounded-3">
        <div className="d-flex align-items-center px-2" aria-hidden>
          <span
            className="d-inline-flex align-items-center justify-content-center fs-0"
            style={{ color: 'var(--falcon-navbar-color)' }}
          >
            <FontAwesomeIcon icon={faWallet} />
          </span>
        </div>

        <WalletDivider />

        <ChildrensTooltip
          delayToRemove={100}
          placement="bottom"
          showOnHover={false}
          html={openMenu === 'personal' ? undefined : 'کیف پول شخصی'}
        >
          <Dropdown
            show={openMenu === 'personal'}
            onToggle={nextShow => setOpenMenu(nextShow ? 'personal' : null)}
            align="end"
          >
            <Dropdown.Toggle
              id="wallets-nav-personal-toggle"
              bsPrefix="toggle"
              as="div"
              role="button"
              tabIndex={0}
              className="d-flex align-items-center gap-2 py-1 cursor-pointer hover-bg-300"
              style={{ paddingInline: '0.65rem' }}
            >
              <span className="d-flex flex-column align-items-start gap-1 lh-sm">
                <span
                  className="d-inline-flex align-items-center text-600 text-nowrap"
                  style={{ fontSize: '0.65rem' }}
                >
                  شخصی
                  <span
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <HelpTooltip
                      html={personalWalletHelp}
                      placement="bottom"
                      className="ms-1"
                      styles={{ fontSize: '0.55rem' }}
                    />
                  </span>
                </span>
                <BalanceAmount
                  amount={personalBalance}
                  loading={balanceLoading}
                />
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-600${openMenu === 'personal' ? ' fa-rotate-180' : ''}`}
                style={{ fontSize: '0.55rem' }}
                aria-hidden
              />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{ strategy: 'fixed' }}
              renderOnMount
              className="mt-2 bg-200"
            >
              {personalMenuItems.map(item => (
                <Dropdown.Item
                  key={item.title}
                  as={'action' in item && item.action ? 'button' : Link}
                  {...(!('action' in item && item.action) && {
                    to: 'to' in item ? item.to : undefined,
                  })}
                  onClick={() => {
                    setOpenMenu(null);
                    if (item.action === 'openChargeModal') {
                      setShowChargeModal(true);
                    }
                    if (item.action === 'openLegalModal') {
                      setShowLegalModal(true);
                    }
                  }}
                  className="d-flex align-items-center py-2 rounded-2 link-600"
                >
                  <FontAwesomeIcon icon={item.icon} className="me-2" />
                  <span>{item.title}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </ChildrensTooltip>

        <WalletDivider />

        <ChildrensTooltip
          delayToRemove={100}
          placement="bottom"
          showOnHover={false}
          html={openMenu === 'ai' ? undefined : 'کیف پول AI'}
        >
          <Dropdown
            show={openMenu === 'ai'}
            onToggle={nextShow => setOpenMenu(nextShow ? 'ai' : null)}
            align="end"
          >
            <Dropdown.Toggle
              id="wallets-nav-ai-toggle"
              bsPrefix="toggle"
              as="div"
              role="button"
              tabIndex={0}
              className="d-flex align-items-center gap-2 py-1 cursor-pointer hover-bg-300"
              style={{ paddingInline: '0.65rem' }}
            >
              <span className="d-flex flex-column align-items-start gap-1 lh-sm">
                <span
                  className="d-inline-flex align-items-center text-600 text-nowrap"
                  style={{ fontSize: '0.65rem' }}
                >
                  AI
                  <span
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <HelpTooltip
                      html={aiWalletHelp}
                      placement="bottom"
                      className="ms-1"
                      styles={{ fontSize: '0.55rem' }}
                    />
                  </span>
                </span>
                <BalanceAmount amount={aiBalance} loading={balanceLoading} />
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-600${openMenu === 'ai' ? ' fa-rotate-180' : ''}`}
                style={{ fontSize: '0.55rem' }}
                aria-hidden
              />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{ strategy: 'fixed' }}
              renderOnMount
              className="mt-2 bg-200"
            >
              {aiMenuItems.map(item => (
                <Dropdown.Item
                  key={item.title}
                  as={'action' in item && item.action ? 'button' : Link}
                  {...(!('action' in item && item.action) && {
                    to: 'to' in item ? item.to : undefined,
                  })}
                  onClick={() => {
                    setOpenMenu(null);
                    if ('action' in item && item.action === 'openGiftCodeModal') {
                      setShowGiftCodeModal(true);
                    }
                  }}
                  className="d-flex align-items-center py-2 rounded-2 link-600"
                >
                  <FontAwesomeIcon icon={item.icon} className="me-2" />
                  <span>{item.title}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </ChildrensTooltip>
      </div>

      <ChargeWalletModal
        show={showChargeModal}
        onHide={() => setShowChargeModal(false)}
      />
      <GiftCodeModal
        show={showGiftCodeModal}
        onHide={() => setShowGiftCodeModal(false)}
      />
      <LegalModalContainer
        show={showLegalModal}
        setShow={setShowLegalModal}
      />
    </>
  );
};

export default WalletsNavGroup;
